import type { PreTriageCondition, PreTriageEvaluationResult, PreTriageExternalOutcome, PreTriageOutcome, PreTriageSummaryItem } from '@core/entities/PreTriage';
import type { PreTriageGatewayBundle, PreTriageGatewayBundleResource, PreTriageGatewayResult } from '@core/ports/PreTriageGateway';
import {
  GuidanceResponseStatusEnum,
  Parameters,
  type ParametersParameter,
} from '@core/fhir-r4/fhir';

export class R4PreTriageOutcomeParser {
  private static readonly outcomeSystem = 'urn:inav:pre-triage:outcome';

  private static readonly infermedicaOutcomeSystem = 'urn:inav:infermedica:outcome';

  private static readonly redirectPathExtensionUrl = 'urn:inav:pre-triage:redirect-path';

  public parse(result: PreTriageGatewayResult): PreTriageEvaluationResult {
    const { guidanceResponse, bundle } = result;

    if (guidanceResponse.status !== GuidanceResponseStatusEnum.SUCCESS) {
      throw new Error('The pre-triage response did not contain a completed outcome.');
    }

    const reasonCode = guidanceResponse.reasonCode?.[0];
    const outcomeCoding = reasonCode?.coding?.find((coding) => coding.system === R4PreTriageOutcomeParser.outcomeSystem)
      ?? reasonCode?.coding?.[0];
    const outcome = this.getOutcome(outcomeCoding?.code, outcomeCoding?.system);
    const isInfermedicaOutcome = outcomeCoding?.system === R4PreTriageOutcomeParser.infermedicaOutcomeSystem;

    const redirectExtension = guidanceResponse.extension?.find(
      (extension) => extension.url === R4PreTriageOutcomeParser.redirectPathExtensionUrl,
    );

    const noteTexts = (guidanceResponse.note ?? [])
      .map((note) => note.text?.trim())
      .filter((noteText): noteText is string => Boolean(noteText));
    const conditions = isInfermedicaOutcome
      ? this.extractConditions(bundle)
      : [];
    const summaryItems = isInfermedicaOutcome
      ? this.extractSummaryItems(guidanceResponse)
      : [];
    const doItems = isInfermedicaOutcome
      ? this.buildDoItems(summaryItems)
      : [];
    const dontItems = isInfermedicaOutcome && outcome === 'stop'
      ? ['Do not delay seeking urgent care if your symptoms suddenly get worse.']
      : [];
    const tasks = isInfermedicaOutcome
      ? this.buildTasks(summaryItems)
      : [];
    const externalOutcome = this.getExternalOutcome(outcomeCoding?.code, outcomeCoding?.system);

    const redirectPath = redirectExtension?.valueUrl ?? redirectExtension?.valueString ?? undefined;

    return {
      outcome,
      title: reasonCode?.text ?? outcomeCoding?.display ?? 'Pre-triage result',
      message: guidanceResponse.note?.[0]?.text ?? 'Pre-triage result.',
      ...(externalOutcome ? { externalOutcome } : {}),
      redirectPath,
      ...(conditions.length > 0 ? { conditions } : {}),
      ...(summaryItems.length > 0 ? { summaryItems } : {}),
      ...(isInfermedicaOutcome && noteTexts.length > 0 ? { details: noteTexts } : {}),
      ...(doItems.length > 0 ? { doItems } : {}),
      ...(dontItems.length > 0 ? { dontItems } : {}),
      ...(tasks.length > 0 ? { tasks } : {}),
    };
  }

  private extractSummaryItems(guidanceResponse: { contained?: unknown[]; outputParameters?: { reference?: string } }): readonly PreTriageSummaryItem[] {
    const containedParameters = this.getContainedParameters(guidanceResponse);

    if (!containedParameters?.parameter?.length) {
      return [];
    }

    return containedParameters.parameter
      .map((parameter) => this.mapSummaryItem(parameter))
      .filter((item): item is PreTriageSummaryItem => item !== null);
  }

  private getContainedParameters(guidanceResponse: { contained?: unknown[]; outputParameters?: { reference?: string } }): Parameters | undefined {
    const parametersResources = (guidanceResponse.contained ?? [])
      .filter((resource): resource is Parameters => resource instanceof Parameters);

    const outputReference = guidanceResponse.outputParameters?.reference?.trim();
    if (outputReference) {
      const normalizedReference = outputReference.startsWith('#')
        ? outputReference.slice(1)
        : outputReference;
      const matchedParameters = parametersResources.find((resource) => resource.id === normalizedReference);
      if (matchedParameters) {
        return matchedParameters;
      }
    }

    return parametersResources.length === 1
      ? parametersResources[0]
      : undefined;
  }

  private extractConditions(bundle?: PreTriageGatewayBundle): readonly PreTriageCondition[] {
    if (!bundle?.entry?.length) {
      return [];
    }

    return bundle.entry
      .map((entry) => this.mapCondition(entry.resource))
      .filter((condition): condition is PreTriageCondition => condition !== null);
  }

  private mapCondition(resource?: PreTriageGatewayBundleResource): PreTriageCondition | null {
    if (!resource || resource.resourceType !== 'Condition') {
      return null;
    }

    const name = this.readConditionName(resource);
    if (!name) {
      return null;
    }

    const noteTexts = this.readConditionNoteTexts(resource);
    const commonName = this.readPrefixedNoteValue(noteTexts, 'Common name:');
    const probability = this.readConditionProbability(noteTexts);

    return {
      name,
      ...(commonName && commonName !== name ? { commonName } : {}),
      ...(probability !== undefined
        ? {
          probability,
          probabilityLabel: this.formatProbabilityLabel(probability),
        }
        : {}),
    };
  }

  private readConditionName(resource: PreTriageGatewayBundleResource): string | undefined {
    const code = resource.code;
    if (!code || typeof code !== 'object') {
      return undefined;
    }

    const text = (code as { text?: unknown }).text;
    if (typeof text === 'string' && text.trim()) {
      return text.trim();
    }

    const coding = (code as { coding?: unknown }).coding;
    if (!Array.isArray(coding)) {
      return undefined;
    }

    for (const codingEntry of coding) {
      if (!codingEntry || typeof codingEntry !== 'object') {
        continue;
      }

      const display = (codingEntry as { display?: unknown }).display;
      if (typeof display === 'string' && display.trim()) {
        return display.trim();
      }

      const codeValue = (codingEntry as { code?: unknown }).code;
      if (typeof codeValue === 'string' && codeValue.trim()) {
        return codeValue.trim();
      }
    }

    return undefined;
  }

  private readConditionNoteTexts(resource: PreTriageGatewayBundleResource): readonly string[] {
    const note = resource.note;
    if (!Array.isArray(note)) {
      return [];
    }

    return note
      .map((noteEntry) => {
        if (!noteEntry || typeof noteEntry !== 'object') {
          return undefined;
        }

        const text = (noteEntry as { text?: unknown }).text;
        return typeof text === 'string'
          ? text.trim()
          : undefined;
      })
      .filter((text): text is string => Boolean(text));
  }

  private readPrefixedNoteValue(noteTexts: readonly string[], prefix: string): string | undefined {
    const matchingNote = noteTexts.find((noteText) => noteText.startsWith(prefix));
    if (!matchingNote) {
      return undefined;
    }

    const value = matchingNote.slice(prefix.length).trim();
    return value || undefined;
  }

  private readConditionProbability(noteTexts: readonly string[]): number | undefined {
    const probabilityText = this.readPrefixedNoteValue(noteTexts, 'Probability:');
    if (!probabilityText) {
      return undefined;
    }

    const parsedProbability = Number.parseFloat(probabilityText);
    return Number.isFinite(parsedProbability)
      ? parsedProbability
      : undefined;
  }

  private formatProbabilityLabel(probability: number): string {
    const percentage = probability <= 1
      ? probability * 100
      : probability;
    const roundedPercentage = Math.round(percentage * 10) / 10;
    const decimals = Number.isInteger(roundedPercentage)
      ? 0
      : 1;

    return `Probability: ${roundedPercentage.toFixed(decimals)}%`;
  }

  private getExternalOutcome(code: string | undefined, system: string | undefined): PreTriageExternalOutcome | undefined {
    if (system !== R4PreTriageOutcomeParser.infermedicaOutcomeSystem) {
      return undefined;
    }

    switch (code) {
      case 'self_care':
      case 'consultation':
      case 'consultation_24':
      case 'emergency':
      case 'emergency_ambulance':
        return code;
      default:
        return undefined;
    }
  }

  private mapSummaryItem(parameter: ParametersParameter): PreTriageSummaryItem | null {
    const rawName = parameter.name?.trim();
    const value = parameter.valueCodeableConcept?.text?.trim()
      ?? parameter.valueCodeableConcept?.coding?.[0]?.display?.trim()
      ?? parameter.valueCodeableConcept?.coding?.[0]?.code?.trim();

    if (!rawName || !value) {
      return null;
    }

    return {
      key: this.toSummaryLabel(rawName),
      value,
    };
  }

  private toSummaryLabel(rawName: string): string {
    switch (rawName) {
      case 'recommended-specialist':
        return 'Recommended specialist';
      case 'recommended-channel':
        return 'Recommended channel';
      case 'recommendation-message':
        return 'Recommendation';
      default:
        return rawName
          .split('-')
          .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
          .join(' ');
    }
  }

  private buildDoItems(
    summaryItems: readonly PreTriageSummaryItem[],
  ): readonly string[] {
    const items: string[] = [];

    for (const summaryItem of summaryItems) {
      if (summaryItem.key === 'Recommended specialist') {
        items.push(`Contact the recommended specialist: ${summaryItem.value}.`);
      }

      if (summaryItem.key === 'Recommended channel') {
        items.push(`Use the recommended care channel: ${summaryItem.value}.`);
      }
    }

    return items;
  }

  private buildTasks(
    summaryItems: readonly PreTriageSummaryItem[],
  ): readonly string[] {
    if (summaryItems.length === 0) {
      return [];
    }

    const tasks: string[] = ['Review the recommendation summary.'];

    const specialist = summaryItems.find((item) => item.key === 'Recommended specialist')?.value;
    if (specialist) {
      tasks.push(`Contact ${specialist}.`);
    }

    const channel = summaryItems.find((item) => item.key === 'Recommended channel')?.value;
    if (channel) {
      tasks.push(`Use ${channel} as the next care route.`);
    }

    return tasks;
  }

  private getOutcome(outcomeCode: string | undefined, outcomeSystem: string | undefined): PreTriageOutcome {
    if (outcomeCode === 'continue' || outcomeCode === 'stop' || outcomeCode === 'redirect') {
      return outcomeCode;
    }

    if (outcomeSystem === R4PreTriageOutcomeParser.infermedicaOutcomeSystem && outcomeCode) {
      return 'stop';
    }

    throw new Error('The pre-triage outcome response did not include a supported outcome code.');
  }
}